/**
 * LiquidGlassTransition — the physical-material state machine.
 *
 * This module owns *only* the physical state of the Liquid Glass system
 * (resting → fluidizing → drop → travel → flatten → solid → and reverse).
 * It knows nothing about guests, journeys, or content — that lives in the
 * business state layer above it.
 *
 * The state machine is exposed as a plain React hook. The rendering
 * components (PresenceCube, JourneyVessel) subscribe to `phase` and
 * `phaseStartMs` and interpolate their own visuals frame-by-frame in
 * `useFrame`. This keeps physics deterministic and reversible while
 * letting each mesh decide how to express the phase.
 *
 * Reduced-motion mode short-circuits the intermediate phases: forward
 * jumps resting → stable; reverse jumps stable → resting. The vessel
 * still appears/disappears with a subtle fade + scale from within the
 * mesh components; it is never absent.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Physical phases and how long each takes before it advances on its own.
 * Durations are in seconds. `Infinity` means the phase is a stable
 * resting point (only external input advances it).
 *
 * Two directions of flow:
 *   forward: resting → fluidizing → separating → traveling → flattening → solidifying → stable
 *   reverse: stable  → softening  → contracting → returning → merging     → resting
 */
export const PHASES = Object.freeze({
  resting:     { next: null,           duration: Infinity, direction: "idle" },
  fluidizing:  { next: "separating",   duration: 0.9,      direction: "forward" },
  separating:  { next: "traveling",    duration: 0.9,      direction: "forward" },
  traveling:   { next: "flattening",   duration: 1.3,      direction: "forward" },
  flattening:  { next: "solidifying",  duration: 1.1,      direction: "forward" },
  solidifying: { next: "stable",       duration: 0.7,      direction: "forward" },
  stable:      { next: null,           duration: Infinity, direction: "idle" },
  softening:   { next: "contracting",  duration: 0.7,      direction: "reverse" },
  contracting: { next: "returning",    duration: 1.1,      direction: "reverse" },
  returning:   { next: "merging",      duration: 1.3,      direction: "reverse" },
  merging:     { next: "resting",      duration: 0.7,      direction: "reverse" },
});

export const FORWARD_PHASES = [
  "resting", "fluidizing", "separating", "traveling", "flattening", "solidifying", "stable",
];

export const REVERSE_PHASES = [
  "stable", "softening", "contracting", "returning", "merging", "resting",
];

/**
 * Positions used across mesh components. Kept here so the physical
 * layer and the visual layer read the same numbers.
 */
export const SCENE = Object.freeze({
  cubePosition:   [-1.4, 0, 0],
  cubeSurfaceX:   -0.65,  // right face of the cube (drop origin/return point)
  vesselPosition: [ 1.15, 0, 0],
  cubeSize:       1.5,
  slabScale:      [1.75, 1.75, 0.18],
  dropScale:      [0.42, 0.42, 0.42],
});

/**
 * Detect the OS reduced-motion preference once.
 */
function readSystemReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * useLiquidGlassTransition — the hook every consumer subscribes to.
 *
 * Returns:
 *   phase          — current physical phase (string, keys of PHASES)
 *   phaseStartMs   — performance.now() at the moment the phase started
 *   direction      — "idle" | "forward" | "reverse"
 *   reducedMotion  — boolean (system default OR user override)
 *   triggerCheckIn / triggerCheckout / reset — external inputs
 *   setReducedMotion — user override (persisted only for the session)
 */
export function useLiquidGlassTransition() {
  const [phase, setPhase] = useState("resting");
  const [phaseStartMs, setPhaseStartMs] = useState(() => performance.now());
  const [reducedMotion, setReducedMotion] = useState(readSystemReducedMotion);
  const timerRef = useRef(null);

  const advanceTo = useCallback((next) => {
    setPhase(next);
    setPhaseStartMs(performance.now());
  }, []);

  const triggerCheckIn = useCallback(() => {
    if (phase !== "resting") return;
    if (reducedMotion) advanceTo("stable");
    else advanceTo("fluidizing");
  }, [phase, reducedMotion, advanceTo]);

  const triggerCheckout = useCallback(() => {
    if (phase !== "stable") return;
    if (reducedMotion) advanceTo("resting");
    else advanceTo("softening");
  }, [phase, reducedMotion, advanceTo]);

  const reset = useCallback(() => advanceTo("resting"), [advanceTo]);

  // Auto-advance phases that have a `next`.
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const spec = PHASES[phase];
    if (!spec || !spec.next) return;
    timerRef.current = setTimeout(() => advanceTo(spec.next), spec.duration * 1000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [phase, advanceTo]);

  const direction = PHASES[phase]?.direction || "idle";

  return useMemo(() => ({
    phase,
    phaseStartMs,
    direction,
    reducedMotion,
    setReducedMotion,
    triggerCheckIn,
    triggerCheckout,
    reset,
    SCENE,
    PHASES,
  }), [phase, phaseStartMs, direction, reducedMotion, triggerCheckIn, triggerCheckout, reset]);
}

/**
 * phaseProgress(phase, phaseStartMs, nowMs?) → 0..1
 *
 * Small helper used by mesh components inside useFrame to know how far
 * along the current phase they are. Stable and merging progress is
 * always 0 (idle phases).
 */
export function phaseProgress(phase, phaseStartMs, nowMs = performance.now()) {
  const spec = PHASES[phase];
  if (!spec || spec.duration === Infinity) return 1; // stable/resting sit at 1.
  const t = (nowMs - phaseStartMs) / (spec.duration * 1000);
  return Math.max(0, Math.min(1, t));
}

/**
 * A tiny easing that reads more like a settling liquid than a linear
 * tween: slow-in, slow-out, but not overshoot. Never elastic.
 */
export function easeSettle(t) {
  return t < 0.5
    ? 2 * t * t
    : 1 - Math.pow(-2 * t + 2, 2) / 2;
}
